/**
 * Reset-password page. Two states:
 *
 * 1. Happy path — user arrives from a valid email link with `?token=xxx`
 *    in the URL. They enter a new password and confirm; the server
 *    validates the token and updates the password. After success, redirect
 *    to /login. BetterAuth doesn't auto-sign-in after reset (different from
 *    autoSignInAfterVerification on email-verify; password reset is
 *    sensitive and re-auth proves the user still controls the credentials).
 *
 * 2. Expired/invalid token — BetterAuth's verify-callback redirects with
 *    `?error=INVALID_TOKEN` (and strips the token). We show a "request a
 *    new link" form, pre-filling the email from the `?hint=` param that
 *    `mintPasswordResetUrl` (server-side) embedded in the original
 *    callback URL. User clicks send → standard requestPasswordReset flow
 *    fires off a fresh email. No retyping.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { authClient } from '@/shared/auth/authClient';
import { Button, Form, FormTextField } from '@/interfaces/learner/ui';
import LoadingIcon from '@/foundation/ui/LoadingIcon';
import AuthLayout from '@/shared/auth/AuthLayout';
import Logomark from '@/shared/components/Logomark';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

const resetSchema = z
  .object({
    password: z.string().min(8, 'At least 8 characters').max(128, 'At most 128 characters'),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: "Passwords don't match",
  });
type ResetValues = z.infer<typeof resetSchema>;

const requestSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});
type RequestValues = z.infer<typeof requestSchema>;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const error = searchParams.get('error');
  // The email hint comes from the SPA-side callback URL we embed when
  // generating the reset link (see core/userService mintPasswordResetUrl).
  // Used purely to pre-fill the recovery form — never trusted as a
  // source of truth.
  const hintedEmail = searchParams.get('hint') ?? '';
  const [requestSent, setRequestSent] = useState(false);

  const resetForm = useForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirm: '' },
    mode: 'onBlur',
  });
  const passwordValue = resetForm.watch('password');

  const requestForm = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: hintedEmail },
    mode: 'onBlur',
  });

  // ─── Recovery flow: token missing or invalid ─────────────────────────────

  const showRecoveryFlow = !token || error;

  if (showRecoveryFlow) {
    if (requestSent) {
      return (
        <AuthLayout>
          <div className="text-center mb-8">
            <div className="mb-4 flex justify-center">
              <Logomark size={48} />
            </div>
            <h1 className="text-2xl font-bold">Check your email</h1>
          </div>
          <div className="space-y-4 text-center">
            <p className="text-sm">
              If an account exists for that email, we&apos;ve sent a fresh reset link.
            </p>
            <div className="text-sm pt-2">
              <Link to="/login" className="text-[var(--color-accent-text)] hover:underline">
                Back to sign in
              </Link>
            </div>
          </div>
        </AuthLayout>
      );
    }

    const onRequest: SubmitHandler<RequestValues> = async ({ email }) => {
      const { error: reqError } = await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (reqError) {
        toast.error(reqError.message ?? 'Could not send reset email');
        return;
      }
      setRequestSent(true);
    };

    const requestSubmitting = requestForm.formState.isSubmitting;
    // Differentiate the heading + copy depending on whether we got here
    // because the link was missing entirely vs because it was rejected.
    const heading = error ? 'This reset link has expired' : 'Invalid reset link';
    const intro = error
      ? 'Reset links are valid for 24 hours. Send yourself a fresh one — your account will stay the way it is.'
      : 'This reset link is missing its token. Request a new one and we’ll email it to you.';

    return (
      <AuthLayout>
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <Logomark size={48} />
          </div>
          <h1 className="text-2xl font-bold">{heading}</h1>
        </div>
        <Form onSubmit={requestForm.handleSubmit(onRequest)} className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">{intro}</p>
          <FormTextField
            control={requestForm.control}
            name="email"
            label="Email"
            type="email"
            autoComplete="email"
            isRequired
            placeholder="you@example.com"
          />
          <Button type="submit" isDisabled={requestSubmitting} className="w-full">
            {requestSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingIcon size="sm" />
                Sending...
              </span>
            ) : (
              'Send fresh reset link'
            )}
          </Button>
          <div className="text-center text-sm pt-2">
            <Link to="/login" className="text-[var(--color-accent-text)] hover:underline">
              Back to sign in
            </Link>
          </div>
        </Form>
      </AuthLayout>
    );
  }

  // ─── Happy path: valid token, set new password ──────────────────────────

  const onSubmit: SubmitHandler<ResetValues> = async ({ password }) => {
    const { error: resetError } = await authClient.resetPassword({
      newPassword: password,
      token,
    });
    if (resetError) {
      // The server returns INVALID_TOKEN here when the token expired
      // between the verify-callback redirect and the user submitting the
      // form (rare but possible if they sit on the page for an hour).
      // Re-render the recovery flow on next render by navigating to the
      // page WITH the error param, preserving the hint.
      const hintParam = hintedEmail ? `&hint=${encodeURIComponent(hintedEmail)}` : '';
      navigate(`/reset-password?error=INVALID_TOKEN${hintParam}`, { replace: true });
      toast.error('This link expired. Send yourself a fresh one.');
      return;
    }
    toast.success('Password reset. Please sign in.');
    navigate('/login', { replace: true });
  };

  const submitting = resetForm.formState.isSubmitting;

  return (
    <AuthLayout>
      <div className="text-center mb-8">
        <div className="mb-4 flex justify-center">
          <Logomark size={48} />
        </div>
        <h1 className="text-2xl font-bold">Set a new password</h1>
      </div>
      <Form onSubmit={resetForm.handleSubmit(onSubmit)} className="space-y-4">
        <FormTextField
          control={resetForm.control}
          name="password"
          label="New password"
          type="password"
          autoComplete="new-password"
          isRequired
          placeholder="At least 8 characters"
        />
        <PasswordStrengthMeter password={passwordValue ?? ''} />
        <FormTextField
          control={resetForm.control}
          name="confirm"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          isRequired
        />
        {/* No captcha widget on the happy path — BetterAuth's captcha plugin
            defaults gate /sign-up/email, /sign-in/email, and
            /request-password-reset only. /reset-password (the token-
            consuming endpoint) is NOT captcha-gated. The widget on the
            recovery form above (mirroring ForgotPasswordPage) is what
            matters when the user requests a fresh link. */}
        <Button type="submit" isDisabled={submitting} className="w-full">
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingIcon size="sm" />
              Resetting...
            </span>
          ) : (
            'Reset password'
          )}
        </Button>
      </Form>
    </AuthLayout>
  );
}
